import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import store from '@/store/index.js'
import { glReconciliationHeaders } from '@/headers/GLReconciliation.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { companyLogoClient, PdfLogoDimensions } from '@/utils/CompanyLogo.js'
import { alternatingRowColorsLayout } from '@/utils/pdf-formatters/pdfHelpers.js'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const noBorder = [false, false, false, false]
const bottomBorder = [false, false, false, true]
// Roboto (pdfmake's bundled font) has no glyph for U+2016, so the GL pair separator is plain ASCII here
const doubleBar = '||'

const columnWidths = ['30%', '*', '*', '*', '*']

const styles = {
  header: {
    fontSize: 18,
    bold: true
  },
  subHeader: {
    fontSize: 14,
    bold: true
  },
  entityHeader: {
    fontSize: 12,
    bold: true
  },
  groupHeader: {
    fontSize: 10,
    bold: true
  },
  cell: {
    fontSize: 8
  },
  columnHeader: {
    fontSize: 8,
    bold: true
  },
  footer: {
    fontSize: 8,
    margin: [40, 0, 40, 0]
  }
}

export async function createGLReconciliationPDF ({ groups, scopeLine, footerText, filename }) {
  const companyInfo = store.getters['user/companyInfo']
  const logoAsDataUrl = await companyLogoClient.getCompanyLogoAsDataUrl(companyInfo.companyInfoId)

  const entities = getEntities(groups ?? [])
  const docDefinition = {
    pageOrientation: 'landscape',
    content: [
      getTitleBlock(companyInfo, scopeLine, logoAsDataUrl),
      ...(entities.length === 0
        ? [{ text: 'No GL activity for this selection.', margin: [0, 12, 0, 0] }]
        : entities.flatMap(getEntityContent))
    ],
    styles,
    footer: currentPage => ({
      columns: [
        { text: `${currentPage}`, style: 'footer', width: '*' },
        { text: footerText, style: 'footer', width: 'auto' }
      ]
    })
  }

  PDFMake.createPdf(docDefinition).download(filename)
}

function getTitleBlock (companyInfo, scopeLine, logoAsDataUrl) {
  const summaryStack = []
  if (logoAsDataUrl) {
    summaryStack.push({
      image: logoAsDataUrl,
      fit: [PdfLogoDimensions.LOGO_MAX_WIDTH, PdfLogoDimensions.LOGO_MAX_HEIGHT],
      alignment: 'right'
    })
  }
  summaryStack.push({
    text: `Generated ${utcToLocalDate(new Date().toISOString(), 'L - LT')}`,
    style: 'cell',
    alignment: 'right',
    margin: [0, summaryStack.length > 0 ? 4 : 0, 0, 0]
  })

  return {
    columns: [
      {
        stack: [
          { text: companyInfo?.name ?? '', style: 'cell' },
          { text: 'GL Reconciliation Register', style: 'header' },
          { text: scopeLine, style: 'subHeader', margin: [0, 2, 0, 0] }
        ]
      },
      { stack: summaryStack, width: 'auto' }
    ],
    margin: [0, 0, 0, 4]
  }
}

function getEntityContent (entity, entityIndex) {
  const grandTotal = entity.groups.reduce((total, group) => total + group.amount, 0)

  return [
    {
      columns: [
        { text: entity.businessEntityName, style: 'entityHeader' },
        {
          text: `GL Pairs: ${entity.groups.length}\nGrand Total: ${formatMoney(grandTotal)}`,
          width: 'auto',
          style: 'entityHeader',
          alignment: 'right'
        }
      ],
      pageBreak: entityIndex > 0 ? 'before' : ''
    },
    ...entity.groups.flatMap(getGroupContent)
  ]
}

function getGroupContent (group) {
  return [
    {
      columns: [
        {
          text: group.glOffset ? `${group.glCode} ${doubleBar} ${group.glOffset}` : group.glCode,
          style: 'groupHeader',
          margin: [0, 8, 16, 0]
        },
        {
          text: `Amount: ${formatMoney(group.amount)}\nNet Tons: ${numberWithCommas(group.netTons, 3)}\nNet MBF: ${numberWithCommas(group.netMbf, 3)}`,
          width: 'auto',
          style: 'groupHeader',
          alignment: 'right',
          margin: [0, 8, 0, 0]
        }
      ]
    },
    {
      table: {
        headerRows: 1,
        keepWithHeaderRows: 1,
        widths: columnWidths,
        body: getTableRows(group.accounts ?? [])
      },
      layout: alternatingRowColorsLayout,
      margin: [0, 4, 0, 0]
    }
  ]
}

function getTableRows (accounts) {
  const headers = glReconciliationHeaders()
  const rows = [headers.map(header => ({
    text: header.text,
    style: 'columnHeader',
    border: bottomBorder,
    alignment: header.align
  }))]

  accounts.forEach(account => {
    rows.push(headers.map(header => ({
      text: header.format ? header.format(account[header.value]) : account[header.value],
      style: 'cell',
      border: noBorder,
      alignment: header.align
    })))
  })

  return rows
}

function getEntities (groups) {
  const entities = new Map()

  for (const group of groups) {
    let entity = entities.get(group.businessEntityId)
    if (entity === undefined) {
      entity = {
        businessEntityId: group.businessEntityId,
        businessEntityName: group.businessEntityName,
        groups: []
      }
      entities.set(group.businessEntityId, entity)
    }
    entity.groups.push(group)
  }

  return [...entities.values()]
}
